/**
 * LLM 추출 프롬프트 관리 페이지 (M14 / F-2) — admin 전용.
 *
 * 기능:
 *   - 현재 active 요약 (없으면 "코드 default 사용 중" 안내).
 *   - 버전 목록 (draft / active / archived) + 상태별 액션:
 *       draft    : 편집 / 활성화 / 삭제 / 테스트
 *       active   : 클론하여 편집 / 테스트
 *       archived : 롤백 / 클론 / 테스트
 *   - 생성(새 draft) / 편집·클론 모달 + dry-run 테스트 모달.
 */

import { useMemo, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import { DEFAULT_PROMPT_NAME, type PromptStatus, type PromptTemplateOut } from "@/lib/api/prompts";
import { cn } from "@/lib/cn";

import PromptEditorModal from "./PromptEditorModal";
import PromptTestModal from "./PromptTestModal";
import {
  useActivatePrompt,
  useDeletePromptDraft,
  usePrompts,
  useRollbackPrompt,
} from "./usePrompts";

function useFormatDate() {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "ko" ? "ko-KR" : "ja-JP";
  return (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleString(locale, { dateStyle: "short", timeStyle: "short" });
  };
}

function StatusBadge({ status }: { status: PromptStatus }) {
  const { t } = useTranslation();
  const cls =
    status === "active"
      ? "bg-emerald-100 text-emerald-700"
      : status === "draft"
        ? "bg-blue-100 text-blue-700"
        : "bg-slate-200 text-slate-500";
  return (
    <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium", cls)}>
      {t(`admin.prompts.status.${status}`)}
    </span>
  );
}

type EditorState =
  | { mode: "create" }
  | { mode: "edit"; prompt: PromptTemplateOut }
  | { mode: "clone"; prompt: PromptTemplateOut }
  | null;

export default function PromptsPage() {
  const { t } = useTranslation();
  const formatDate = useFormatDate();

  const { data, isLoading, error } = usePrompts({ name: DEFAULT_PROMPT_NAME });
  const activateMu = useActivatePrompt();
  const rollbackMu = useRollbackPrompt();
  const deleteMu = useDeletePromptDraft();

  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [editor, setEditor] = useState<EditorState>(null);
  const [testTarget, setTestTarget] = useState<PromptTemplateOut | null>(null);

  const rows = useMemo(() => [...(data ?? [])].sort((a, b) => b.version - a.version), [data]);
  const active = rows.find((r) => r.status === "active") ?? null;

  const onError = (err: unknown) =>
    setActionError(err instanceof ApiError ? err.detail : (err as Error).message);

  const handleActivate = (p: PromptTemplateOut) => {
    setActionError(null);
    setBusyId(p.id);
    activateMu.mutate({ id: p.id }, { onError, onSettled: () => setBusyId(null) });
  };

  const handleRollback = (p: PromptTemplateOut) => {
    setActionError(null);
    setBusyId(p.id);
    rollbackMu.mutate({ archivedId: p.id }, { onError, onSettled: () => setBusyId(null) });
  };

  const handleDelete = (p: PromptTemplateOut) => {
    if (!window.confirm(t("admin.prompts.confirmDelete"))) return;
    setActionError(null);
    setBusyId(p.id);
    deleteMu.mutate(p.id, { onError, onSettled: () => setBusyId(null) });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">{t("admin.prompts.title")}</h1>
          <p className="mt-0.5 text-xs text-slate-500">{t("admin.prompts.description")}</p>
        </div>
        <button
          type="button"
          onClick={() => setEditor({ mode: "create" })}
          className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
        >
          <Plus className="h-3.5 w-3.5" />
          {t("admin.prompts.newDraft")}
        </button>
      </div>

      {/* active 요약 */}
      <div
        className={cn(
          "rounded-md border px-3 py-2 text-xs",
          active
            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
            : "border-slate-200 bg-slate-50 text-slate-500",
        )}
      >
        {active
          ? t("admin.prompts.activeSummary", { version: active.version })
          : t("admin.prompts.noActive")}
      </div>

      {actionError && (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          {actionError}
        </div>
      )}

      <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">{t("admin.prompts.th.version")}</th>
              <th className="px-3 py-2">{t("admin.prompts.th.status")}</th>
              <th className="px-3 py-2">{t("admin.prompts.th.notes")}</th>
              <th className="px-3 py-2">{t("admin.prompts.th.updatedAt")}</th>
              <th className="px-3 py-2 text-right">{t("admin.prompts.th.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-xs text-slate-400">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" aria-hidden />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-xs text-red-500">
                  {t("admin.prompts.error", { message: error.message })}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-xs text-slate-400">
                  {t("admin.prompts.empty")}
                </td>
              </tr>
            ) : (
              rows.map((p) => {
                const isBusy = busyId === p.id;
                return (
                  <tr key={p.id} className="text-slate-700">
                    <td className="px-3 py-2 font-mono text-xs tabular-nums">v{p.version}</td>
                    <td className="px-3 py-2">
                      <StatusBadge status={p.status} />
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {p.notes ? (
                        <span className="line-clamp-2 max-w-[280px]" title={p.notes}>
                          {p.notes}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{formatDate(p.updated_at)}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap justify-end gap-1">
                        <ActionButton onClick={() => setTestTarget(p)}>
                          {t("admin.prompts.actions.test")}
                        </ActionButton>

                        {p.status === "draft" && (
                          <>
                            <ActionButton onClick={() => setEditor({ mode: "edit", prompt: p })}>
                              {t("admin.prompts.actions.edit")}
                            </ActionButton>
                            <ActionButton
                              variant="primary"
                              disabled={isBusy}
                              onClick={() => handleActivate(p)}
                            >
                              {isBusy && <Loader2 className="h-3 w-3 animate-spin" />}
                              {t("admin.prompts.actions.activate")}
                            </ActionButton>
                            <ActionButton
                              variant="danger"
                              disabled={isBusy}
                              onClick={() => handleDelete(p)}
                            >
                              {t("admin.prompts.actions.delete")}
                            </ActionButton>
                          </>
                        )}

                        {p.status === "active" && (
                          <ActionButton onClick={() => setEditor({ mode: "clone", prompt: p })}>
                            {t("admin.prompts.actions.clone")}
                          </ActionButton>
                        )}

                        {p.status === "archived" && (
                          <>
                            <ActionButton
                              variant="primary"
                              disabled={isBusy}
                              onClick={() => handleRollback(p)}
                            >
                              {isBusy && <Loader2 className="h-3 w-3 animate-spin" />}
                              {t("admin.prompts.actions.rollback")}
                            </ActionButton>
                            <ActionButton onClick={() => setEditor({ mode: "clone", prompt: p })}>
                              {t("admin.prompts.actions.clone")}
                            </ActionButton>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {editor?.mode === "create" && (
        <PromptEditorModal mode="create" onClose={() => setEditor(null)} />
      )}
      {editor?.mode === "edit" && (
        <PromptEditorModal mode="edit" prompt={editor.prompt} onClose={() => setEditor(null)} />
      )}
      {editor?.mode === "clone" && (
        <PromptEditorModal mode="clone" prompt={editor.prompt} onClose={() => setEditor(null)} />
      )}
      {testTarget && <PromptTestModal prompt={testTarget} onClose={() => setTestTarget(null)} />}
    </div>
  );
}

function ActionButton({
  children,
  onClick,
  disabled,
  variant = "default",
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: "default" | "primary" | "danger";
}) {
  const cls =
    variant === "primary"
      ? "border-blue-300 text-blue-700 hover:bg-blue-50"
      : variant === "danger"
        ? "border-red-200 text-red-700 hover:bg-red-50"
        : "border-slate-300 text-slate-700 hover:bg-slate-50";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] transition disabled:cursor-not-allowed disabled:opacity-40",
        cls,
      )}
    >
      {children}
    </button>
  );
}
